import { httpClient } from '../client';
import {
  Customer,
  SingleCustomerResponse,
  CreateCustomerDTO,
  UpdateCustomerDTO,
} from '../../../types/customer';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import { tokenService } from '../auth/token.service';

class CustomerEndpoints {
  private static instance: CustomerEndpoints;
  private readonly BASE_PATH = `${import.meta.env.VITE_API_BASE_URL}/organisations`;

  private constructor() {}

  static getInstance(): CustomerEndpoints {
    if (!CustomerEndpoints.instance) {
      CustomerEndpoints.instance = new CustomerEndpoints();
    }
    return CustomerEndpoints.instance;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await tokenService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async listCustomers(
    organizationId: string,
    page = 1,
    pageSize = 10,
    sortBy = 'createdAt',
    sortOrder: 'ASC' | 'DESC' = 'DESC',
    searchTerm?: string
  ) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<{
        data: {
          customers: Customer[];
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
        };
      }>(`${this.BASE_PATH}/${organizationId}/customers`, {
        headers,
        params: {
          page,
          pageSize,
          sortBy,
          sortOrder,
          ...(searchTerm?.trim() && { searchTerm: searchTerm.trim() }),
        },
      });

      logger.info('Customers fetched (paginated)');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to fetch customers:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createCustomer(organizationId: string, data: CreateCustomerDTO): Promise<Customer> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<SingleCustomerResponse>(
        `${this.BASE_PATH}/${organizationId}/customers`,
        data,
        { headers }
      );

      logger.info('Customer created successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create customer:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async updateCustomer(
    organizationId: string,
    customerId: number,
    data: UpdateCustomerDTO
  ): Promise<Customer> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<SingleCustomerResponse>(
        `${this.BASE_PATH}/${organizationId}/customers/${customerId}`,
        data,
        { headers }
      );

      logger.info('Customer updated successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update customer:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async deleteCustomer(organizationId: string, customerId: number): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.BASE_PATH}/${organizationId}/customers/${customerId}`, {
        headers,
      });

      logger.info('Customer deleted successfully');
    } catch (error) {
      logger.error('Failed to delete customer:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const customerEndpoints = CustomerEndpoints.getInstance();
